'use client';

import Image from 'next/image';
import { useEffect, type ReactNode } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { RingSizes } from 'content/products/ring/productMap/constants';
import { RingProColors } from 'content/products/ring-pro';
import {
  ensurePickupSelectionHydrated,
  usePickupSelection,
} from './pickupSelection';
import { usePickupCatalogue } from './pickupCatalogue';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { pickupSkuFor } from './pickupSku';
import { formatPickupDate } from './pickupDate';
import tagIcon from 'assets/images/best-buy-pickup/tag.svg';

/** The Best Buy line that replaces "Ships by <date>" on the US Ring PRO buy
 * page while this selection would go out as a pickup order, or null when it
 * ships (render the page's "Ships by" line then). Same gate as the
 * add-to-cart path, so it never claims pickup for a selection that ships.
 *   no store yet:  "Pick up at Best Buy · Choose a store to see your date"
 *   store chosen:  "Ready by <b>October 29</b> at Best Buy <name>" */
export const usePickupEta = (args: {
  store: string | null | undefined;
  color: RingProColors | null;
  size: RingSizes | null;
  engraving: boolean;
}): ReactNode | null => {
  const t = useTranslations('ring-pro');
  const locale = useLocale();
  const selection = usePickupSelection();
  // Re-render when the catalogue lands: pickupSkuFor reads it.
  usePickupCatalogue(args.store === ShopifyStore.US);
  useEffect(() => {
    ensurePickupSelectionHydrated();
  }, []);

  const sku = pickupSkuFor(args);
  if (selection.mode !== 'pickup' || sku === null) return null;

  // A store picked for another colour or size is not this order's store.
  const chosen = selection.store?.sku === sku ? selection.store : null;
  if (!chosen) {
    return <span>{t('ring-pro.bestBuyPickup.pickupEtaNoStore')}</span>;
  }
  const date = chosen.pickup_date
    ? formatPickupDate(chosen.pickup_date, locale, 'long')
    : null;
  return (
    <span>
      {date
        ? t.rich('ring-pro.bestBuyPickup.pickupEtaReady', {
            date,
            name: chosen.name,
            strong: (chunks) => <strong>{chunks}</strong>,
          })
        : t('ring-pro.bestBuyPickup.pickupEtaStore', { name: chosen.name })}
    </span>
  );
};

/** True only while the buy-page line reads "Ready by <date> at Best Buy
 * <name>". The no-store and store-without-date sentences are not this line. */
export const usePickupReadyBy = (args: {
  store: string | null | undefined;
  color: RingProColors | null;
  size: RingSizes | null;
  engraving: boolean;
}): boolean => {
  const selection = usePickupSelection();
  usePickupCatalogue(args.store === ShopifyStore.US);
  useEffect(() => {
    ensurePickupSelectionHydrated();
  }, []);

  const sku = pickupSkuFor(args);
  if (selection.mode !== 'pickup' || sku === null) return false;
  const chosen = selection.store?.sku === sku ? selection.store : null;
  return Boolean(chosen?.pickup_date);
};

/** The tag that replaces the shipping box beside a pickup line.
 * Sized inline: the host rows size their icons by element selectors. */
export const PickupEtaIcon = () => (
  <Image
    src={tagIcon}
    alt=""
    width={16}
    height={16}
    style={{ width: 16, height: 16, flexShrink: 0 }}
  />
);
